<template>
    <div class="mb-3">
        <label>{{ $t('signal_viewer.modals.calculated_signal_modal.titles.select_type_of_calculation_expression')
            }}</label>
        <DxSelectBox :items="expressionsDataSource" :value="'sum_signals'" displayExpr="name" valueExpr="value"
            @selectionChanged="expressionChanged" width="250"></DxSelectBox>
    </div>
    <div class="d-flex align-items-center d-flex gap-2 mb-3">
        <label>{{ $t('signal_viewer.modals.calculated_signal_modal.titles.calculation_expression') }}:</label>
        <DxNumberBox v-if="!showSecondSignal" v-model:value="result.param2"></DxNumberBox>
        <span v-if="!showSecondSignal">{{ paramSeparator }}</span>
        <DxSelectBox :items="signalDataSource" :value="result.param1" displayExpr="name" valueExpr="index"
            @selectionChanged="firstSignalChanged"></DxSelectBox>
        <span v-if="showSecondSignal">{{ paramSeparator }}</span>
        <DxSelectBox v-if="showSecondSignal" :items="signalDataSource" :value="result.param2" displayExpr="name"
            valueExpr="index" @selectionChanged="secondSignalChanged"></DxSelectBox>
    </div>
    <div>
        <label>{{ $t('signal_viewer.modals.calculated_signal_modal.titles.calculated_signals') }}:</label>
    </div>
    <DxCheckBox :text="$t('signal_viewer.modals.calculated_signal_modal.dx_check_boxes.absolute_value')"
        v-model:value="result.components.absoluteValue" :disabled="!anySignalPeriodic" />
    <DxCheckBox :text="$t('signal_viewer.modals.calculated_signal_modal.dx_check_boxes.phase')"
        v-model:value="result.components.phase" :disabled="!anySignalPeriodic" />
    <DxCheckBox :text="$t('signal_viewer.modals.calculated_signal_modal.dx_check_boxes.instant_values')"
        v-model:value="result.components.instantValues" />
</template>

<script setup lang="ts">
import { useIntl } from '@/.shared/services/i18n';
import { DxNumberBox, DxSelectBox, DxCheckBox } from "devextreme-vue";
import type { SelectionChangedEvent } from "devextreme/ui/select_box";
import { useModalBuilder } from "@/.shared/helpers/modal-building-helper";
import { computed, getCurrentInstance, onMounted, reactive, ref, toRaw, watch } from "vue";
import type { CalculatedSignalDialogResult, CalculatedSignalDialogResultExpression } from "./osc";

const i18n = useIntl();
const modal = useModalBuilder();
const props = defineProps<{ signals: { name: string, isPeriodic: boolean }[] }>();
const result = reactive<CalculatedSignalDialogResult>({
    result: "cancel",
    expression: "sum_signals",
    param1: 0,
    param2: 0,
    components: { absoluteValue: true, phase: false, instantValues: false },
});

const expressionsDataSource: { name: string, value: CalculatedSignalDialogResultExpression }[] = [
    { name: i18n.i18n.t('signal_viewer.modals.calculated_signal_modal.expressions_data_sorce.sum_signals'), value: "sum_signals" },
    { name: i18n.i18n.t('signal_viewer.modals.calculated_signal_modal.expressions_data_sorce.subtract_signals'), value: "subtract_signals" },
    { name: i18n.i18n.t('signal_viewer.modals.calculated_signal_modal.expressions_data_sorce.multiplication_by_coef'), value: "multiplication_by_coef" },
];


const signalDataSource = computed(() => {
    return props.signals.map((s, idx) => ({ name: s.name, isPeriodic: s.isPeriodic, index: idx }));
})

const showSecondSignal = computed(() => result.expression === "sum_signals" || result.expression === "subtract_signals");
const paramSeparator = ref<string>("");

const anySignalPeriodic = computed(() => {
    const secondSignalPeriodic = showSecondSignal.value ? props.signals[result.param2].isPeriodic : false;
    const firstSignalPeriodic = props.signals[result.param1].isPeriodic;
    return firstSignalPeriodic || secondSignalPeriodic;
});


watch(anySignalPeriodic, (newVal) => {
    if (!newVal) {
        result.components.phase = false;
        result.components.absoluteValue = false;
    }
}, { immediate: true });

watch(() => result.expression, (newVal, oldVal) => {
    switch (newVal) {
        case "sum_signals":
            if (oldVal === "multiplication_by_coef")
                result.param2 = 0;
            paramSeparator.value = "+";
            break;
        case "subtract_signals":
            if (oldVal === "multiplication_by_coef")
                result.param2 = 0;
            paramSeparator.value = " - ";
            break;
        case "multiplication_by_coef":
            if (oldVal !== "multiplication_by_coef")
                result.param2 = 1;
            paramSeparator.value = " · ";
            break;
    }
}, { immediate: true });

onMounted(() => {
    modal.setContext(getCurrentInstance()!);
    const { okButton, cancelButton } = modal.standardButtons.addOkCancel();

    okButton.update(b => {
        b.onClick = () => {
            result.result = "ok";
            modal.close(toRaw(result));
        };
    });

    cancelButton.update(b => {
        b.onClick = () => {
            result.result = "cancel";
            modal.close(toRaw(result));
        }
    });

});

function expressionChanged(e: SelectionChangedEvent) {
    result.expression = e.selectedItem.value;
}

function firstSignalChanged(e: SelectionChangedEvent) {
    if (showSecondSignal.value)
        result.param1 = e.selectedItem.index;
}

function secondSignalChanged(e: SelectionChangedEvent) {
    result.param2 = e.selectedItem.index;
}
</script>